<script setup lang="ts">
import { onMounted, useTemplateRef, watch } from 'vue';
import useBackend from "@/composition/useBackend";
import { HeifDecoder } from 'libheif-js/wasm-bundle'

const props = defineProps<{
  spaceKey: string
  fileKey: string
}>()

const backend = useBackend()
const imageElem = useTemplateRef<HTMLImageElement>('imageElem')

onMounted(async () => {

  await loadImage()

  watch([props.spaceKey, props.fileKey], loadImage)
})

async function loadImage() {
  try {
    const response = await backend.fetch(
      `/core/resources/files/${encodeURIComponent(props.spaceKey)}/${encodeURIComponent(props.fileKey)}`,
      {
        method: 'GET',
      }
    );

    if (response.ok) {
      const contentType = response.headers.get("content-type")
      let url: string

      if (contentType === 'image/heic') {
        url = await decodeHeic(response)
      } else {
        url = URL.createObjectURL(await response.blob())
      }

      imageElem.value!.src = url
    } else {
      console.error('Could not load image: ', response);
    }
  } catch (error) {
    console.error('Could not load image:', error);
  }
}

async function decodeHeic(response: Response): Promise<string> {
  const decoder = new HeifDecoder()
  const buffer = await response.arrayBuffer()
  const data = decoder.decode(new Uint8Array(buffer))
  const image = data[0]

  const width = image.get_width()
  const height = image.get_height()

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')!
  const imageData = context.createImageData(width, height)

  await new Promise<void>((resolve, reject) => {
    image.display(imageData, (displayData: ImageData | null) => {
      if (!displayData) {
        return reject(new Error('HEIF processing error'))
      }
      resolve()
    })
  })

  context.putImageData(imageData, 0, 0)

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(b => {
      if (b) resolve(b)
      else reject(new Error('Canvas toBlob failed'))
    }, 'image/png')
  })

  return URL.createObjectURL(blob)
}

</script>

<template>
  <img ref="imageElem" />
</template>
